import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ErrorLogModel } from '../models/error-log.model';
import { generateCorrelationId } from '../utils/error.utils';

/**
 * Single, centralized sink for every caught error in the application
 * (services, components, interceptors, guards, GlobalErrorHandlerService).
 *
 * Today this only writes to the console (always in non-production, and as a
 * minimal safety net in production). Keeping one call site means a future
 * remote monitoring sink (e.g. Application Insights/Sentry) can be added here
 * without touching any of the call sites that already depend on it.
 *
 * This service intentionally never throws — logging must never be the cause
 * of a second failure.
 */
@Injectable({ providedIn: 'root' })
export class LoggingService {

  logError(entry: Partial<ErrorLogModel> & Pick<ErrorLogModel, 'errorMessage' | 'category'>): void {
    const normalized: ErrorLogModel = {
      timestamp: new Date().toISOString(),
      correlationId: generateCorrelationId(),
      ...entry
    };

    try {
      if (environment.production) {
        // Production: keep console output minimal but present, so field
        // issues are still triageable from browser dev tools if needed.
        console.error(`[${normalized.category}]`, normalized.errorMessage, normalized);
      } else {
        console.error(`[${normalized.category}]`, normalized);
      }
    } catch {
      // Never let a logging failure mask the original error.
    }
  }
}
